"use client";

import { useState, useRef, useEffect } from "react";
import { useRouter } from "next/navigation";
import { DayPicker, type DateRange } from "react-day-picker";
import { GREEK_CITIES_SORTED, GREEK_CITY_REGIONS } from "@/lib/greek-cities";
import { formatDate, formatDateRange } from "@/lib/format";

/**
 * Sticky search summary above the search results — uses the SAME
 * .search / .search-field DOM and CSS as the homepage hero pill, so
 * both pages render the identical segmented bar (with the active-field
 * pop-out effect, the rich Where suggestions, and the Adults / Children
 * / Infants / Pets stepper popover). A .compact size modifier shrinks
 * the field padding so the bar fits comfortably inside the sticky
 * summary band.
 *
 * The Filters chip sits beside the search pill, outside of the .search
 * container, so it stays available without participating in the pop-out
 * effect.
 */
type Props = {
  initialCity: string;
  initialCheckIn: string;
  initialCheckOut: string;
  initialGuests: string;
  /** Full party breakdown carried over from the URL so the Who
   *  popover opens with the same values the user entered on the
   *  homepage. All optional — if absent, we fall back to splitting
   *  the legacy `guests` count into adults. */
  initialAdults?: string;
  initialChildren?: string;
  initialInfants?: string;
  initialPets?: string;
  extraParams: Record<string, string | string[]>;
  filterCount: number;
  onToggleFilters?: () => void;
};

type ActiveField = "where" | "when" | "who" | null;

const FEATURED_DESTS: { city: string; emoji: string }[] = [
  { city: "Athens", emoji: "🏛" },
  { city: "Mykonos", emoji: "⛵" },
  { city: "Santorini", emoji: "🌅" },
  { city: "Crete", emoji: "🏖" },
  { city: "Thessaloniki", emoji: "🏙" },
  { city: "Naxos", emoji: "🏝" },
  { city: "Corfu", emoji: "🌊" },
  { city: "Rhodes", emoji: "🏰" },
];

export default function SearchSummaryBar({
  initialCity,
  initialCheckIn,
  initialCheckOut,
  initialGuests,
  initialAdults,
  initialChildren,
  initialInfants,
  initialPets,
  extraParams,
  filterCount,
  onToggleFilters,
}: Props) {
  const router = useRouter();
  const [active, setActive] = useState<ActiveField>(null);
  const [city, setCity] = useState(initialCity);
  // Initialise the party breakdown. If the URL carries explicit
  // adults / children / infants / pets, use those directly; otherwise
  // fall back to the legacy `guests` total as adults.
  const initialGuestsNum = Math.max(1, parseInt(initialGuests || "2", 10) || 2);
  const initialAdultsNum = initialAdults
    ? Math.max(1, parseInt(initialAdults, 10) || initialGuestsNum)
    : initialGuestsNum;
  const initialChildrenNum = initialChildren ? Math.max(0, parseInt(initialChildren, 10) || 0) : 0;
  const initialInfantsNum = initialInfants ? Math.max(0, parseInt(initialInfants, 10) || 0) : 0;
  const initialPetsNum = initialPets ? Math.max(0, parseInt(initialPets, 10) || 0) : 0;
  const [adults, setAdults] = useState(initialAdultsNum);
  const [children, setChildren] = useState(initialChildrenNum);
  const [infants, setInfants] = useState(initialInfantsNum);
  const [pets, setPets] = useState(initialPetsNum);
  const guestCount = adults + children;
  const [checkIn, setCheckIn] = useState(initialCheckIn);
  const [checkOut, setCheckOut] = useState(initialCheckOut);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!active) return;
    function onDoc(e: MouseEvent) {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setActive(null);
      }
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setActive(null);
    }
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [active]);

  function buildQuery(over: Partial<{ city: string; checkIn: string; checkOut: string; guests: string }>) {
    const u = new URLSearchParams();
    for (const [k, v] of Object.entries(extraParams)) {
      if (Array.isArray(v)) v.forEach((x) => x && u.append(k, x));
      else if (v) u.set(k, v);
    }
    const next = {
      city: over.city ?? city,
      checkIn: over.checkIn ?? checkIn,
      checkOut: over.checkOut ?? checkOut,
      guests: over.guests ?? String(guestCount),
    };
    if (next.city) u.set("city", next.city);
    else u.delete("city");
    if (next.checkIn) u.set("checkIn", next.checkIn);
    else u.delete("checkIn");
    if (next.checkOut) u.set("checkOut", next.checkOut);
    else u.delete("checkOut");
    if (next.guests) u.set("guests", next.guests);
    else u.delete("guests");
    // Persist the full party breakdown so any refinement keeps the
    // Who popover values across navigations.
    if (adults > 0) u.set("adults", String(adults));
    else u.delete("adults");
    if (children > 0) u.set("children", String(children));
    else u.delete("children");
    if (infants > 0) u.set("infants", String(infants));
    else u.delete("infants");
    if (pets > 0) u.set("pets", String(pets));
    else u.delete("pets");
    u.delete("page");
    return u.toString();
  }

  function go(over: Partial<{ city: string; checkIn: string; checkOut: string; guests: string }> = {}) {
    router.push(`/search?${buildQuery(over)}`);
    setActive(null);
  }

  const whereLabel = city || "Anywhere in Greece";
  const whenLabel = (() => {
    if (checkIn && checkOut) return formatDateRange(checkIn, checkOut);
    if (checkIn) return "Pick check-out";
    return "Add dates";
  })();
  const whoLabel = (() => {
    if (guestCount === 0) return "Add guests";
    const bits = [`${guestCount} guest${guestCount === 1 ? "" : "s"}`];
    if (infants > 0) bits.push(`${infants} infant${infants === 1 ? "" : "s"}`);
    if (pets > 0) bits.push(`${pets} pet${pets === 1 ? "" : "s"}`);
    return bits.join(", ");
  })();

  // Date-picker plumbing for the embedded calendar.
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const range: DateRange | undefined = (() => {
    const f = checkIn ? new Date(checkIn) : undefined;
    const t = checkOut ? new Date(checkOut) : undefined;
    if (!f && !t) return undefined;
    return { from: f, to: t };
  })();
  const midSelection = Boolean(range?.from && !range?.to);
  const minSecondClick = midSelection && range?.from
    ? new Date(range.from.getTime() + 24 * 60 * 60 * 1000)
    : null;
  const dayDisabled = [
    { before: today },
    ...(minSecondClick ? [{ before: minSecondClick }] : []),
  ];

  function handleDateSelect(r: DateRange | undefined) {
    if (!r) {
      setCheckIn("");
      setCheckOut("");
      return;
    }
    // When a complete range is already selected and the user clicks a
    // new date, treat it as the start of a NEW selection. DayPicker can
    // either extend or reset depending on where the click lands —
    // unifying on "new click = fresh from" matches Airbnb / Booking
    // behaviour and stops the picker feeling stuck on the old `from`.
    const hadComplete = Boolean(checkIn && checkOut);
    if (hadComplete && r.from && r.to) {
      const stateDates = new Set([checkIn, checkOut]);
      const fromStr = formatDate(r.from);
      const toStr = formatDate(r.to);
      const clicked =
        !stateDates.has(fromStr) ? r.from : !stateDates.has(toStr) ? r.to : null;
      if (clicked) {
        setCheckIn(formatDate(clicked));
        setCheckOut("");
        return;
      }
    }
    // Same-day double-click → keep `from`, clear `to` so the user can
    // pick a real check-out next.
    if (r.from && r.to && r.to.getTime() === r.from.getTime()) {
      setCheckIn(formatDate(r.from));
      setCheckOut("");
      return;
    }
    // Normal 2-click flow: from then to (in calendar order).
    if (r.from && r.to && r.to < r.from) {
      const start = formatDate(r.to);
      const end = formatDate(r.from);
      setCheckIn(start);
      setCheckOut(end);
      go({ checkIn: start, checkOut: end });
      return;
    }
    setCheckIn(r.from ? formatDate(r.from) : "");
    setCheckOut(r.to ? formatDate(r.to) : "");
    if (r.from && r.to) {
      go({ checkIn: formatDate(r.from), checkOut: formatDate(r.to) });
    }
  }

  const q = city.trim().toLowerCase();
  const filtered = q
    ? GREEK_CITIES_SORTED.filter((c) => c.toLowerCase().includes(q)).slice(0, 8)
    : null;

  return (
    <div className="summary-row" ref={rootRef}>
      <div className={`search compact${active ? ` is-${active}` : ""}`}>
        {/* ── WHERE ──────────────────────────────────────── */}
        <label
          className={`search-field${active === "where" ? " is-active" : ""}`}
          style={{ position: "relative" }}
          onClick={() => setActive("where")}
        >
          <span>Where</span>
          <span className={`search-field-val${city ? "" : " empty"}`}>{whereLabel}</span>

          {active === "where" ? (
            <div
              className="sb-suggest"
              role="listbox"
              onMouseDown={(e) => e.stopPropagation()}
              onClick={(e) => e.stopPropagation()}
            >
              <div style={{ padding: "4px 4px 8px" }}>
                <input
                  autoFocus
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      go();
                    }
                  }}
                  placeholder="Search destinations"
                  autoComplete="off"
                  spellCheck={false}
                  style={{
                    width: "100%",
                    border: "1px solid var(--line-strong)",
                    borderRadius: 10,
                    padding: "10px 14px",
                    fontSize: 14,
                    outline: 0,
                    background: "var(--surface)",
                  }}
                />
              </div>
              {filtered == null ? (
                <>
                  <div className="sb-suggest-header">Suggested destinations</div>
                  {FEATURED_DESTS.map((d) => {
                    const region = GREEK_CITY_REGIONS[d.city];
                    return (
                      <button
                        key={d.city}
                        type="button"
                        className="sb-suggest-row"
                        onClick={() => {
                          setCity(d.city);
                          go({ city: d.city });
                        }}
                      >
                        <span className="sb-suggest-thumb sb-suggest-thumb-emoji">{d.emoji}</span>
                        <span className="sb-suggest-text">
                          <span className="sb-suggest-main">{d.city}, Greece</span>
                          <span className="sb-suggest-sub">
                            {region ? `For ${region.toLowerCase()}-region stays` : "Greece"}
                          </span>
                        </span>
                      </button>
                    );
                  })}
                </>
              ) : filtered.length === 0 ? (
                <div className="sb-suggest-empty">
                  No matches — press Search anyway for &ldquo;{city}&rdquo;.
                </div>
              ) : (
                <>
                  <div className="sb-suggest-header">Matching destinations</div>
                  {filtered.map((c) => {
                    const region = GREEK_CITY_REGIONS[c];
                    return (
                      <button
                        key={c}
                        type="button"
                        className="sb-suggest-row"
                        onClick={() => {
                          setCity(c);
                          go({ city: c });
                        }}
                      >
                        <span className="sb-suggest-thumb sb-suggest-thumb-pin">
                          <svg width="16" height="16" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth={1.5}>
                            <path d="M7 13s-4-3.5-4-7a4 4 0 018 0c0 3.5-4 7-4 7z" />
                            <circle cx="7" cy="6" r="1.5" />
                          </svg>
                        </span>
                        <span className="sb-suggest-text">
                          <span className="sb-suggest-main">{c}, Greece</span>
                          <span className="sb-suggest-sub">{region ?? "Greece"}</span>
                        </span>
                      </button>
                    );
                  })}
                </>
              )}
            </div>
          ) : null}
        </label>

        {/* ── WHEN ───────────────────────────────────────── */}
        <label
          className={`search-field${active === "when" ? " is-active" : ""}`}
          style={{ position: "relative" }}
          onClick={() => setActive("when")}
        >
          <span>When</span>
          <span className={`search-field-val${checkIn && checkOut ? "" : " empty"}`}>
            {whenLabel}
          </span>

          {active === "when" ? (
            <div className="drp-popover drp-popover-summary" onClick={(e) => e.stopPropagation()}>
              <DayPicker
                mode="range"
                selected={range}
                onSelect={handleDateSelect}
                disabled={dayDisabled}
                numberOfMonths={
                  typeof window !== "undefined" &&
                  window.matchMedia("(max-width: 720px)").matches
                    ? 1
                    : 2
                }
                weekStartsOn={1}
              />
              {midSelection ? (
                <p style={{ fontSize: 12, color: "var(--ink-3)", margin: "8px 0 0", padding: "0 6px" }}>
                  Now pick a check-out date.
                </p>
              ) : null}
            </div>
          ) : null}
        </label>

        {/* ── WHO ────────────────────────────────────────── */}
        <label
          className={`search-field${active === "who" ? " is-active" : ""}`}
          style={{ position: "relative" }}
          onClick={() => setActive("who")}
        >
          <span>Who</span>
          <span className={`search-field-val${guestCount === 0 ? " empty" : ""}`}>{whoLabel}</span>

          {active === "who" ? (
            <div className="sb-who" onClick={(e) => e.stopPropagation()}>
              <StepperRow
                title="Adults"
                sub="Ages 13 or above"
                value={adults}
                min={1}
                onChange={setAdults}
              />
              <StepperRow
                title="Children"
                sub="Ages 2 — 12"
                value={children}
                min={0}
                onChange={setChildren}
              />
              <StepperRow
                title="Infants"
                sub="Under 2"
                value={infants}
                min={0}
                onChange={setInfants}
              />
              <StepperRow
                title="Pets"
                sub="Check the listing's pet policy"
                value={pets}
                min={0}
                onChange={setPets}
                last
              />
            </div>
          ) : null}
        </label>

        <button
          type="button"
          className="search-btn"
          aria-label="Search"
          onClick={() => go()}
        >
          <svg width="16" height="16" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth={2}>
            <circle cx={6} cy={6} r={4} />
            <path d="M9 9l4 4" />
          </svg>
          <span className="search-btn-label">Search</span>
        </button>
      </div>

      {/* Filters chip — sits outside the .search pill so it doesn't
          participate in the active-pop-out effect. */}
      <button
        type="button"
        className="chip filter-toggle"
        onClick={onToggleFilters}
      >
        <svg width="13" height="13" viewBox="0 0 13 13" fill="none" stroke="currentColor" strokeWidth={1.5}>
          <path d="M2 3h9M3.5 6.5h6M5 10h3" />
        </svg>
        <span className="filter-toggle-label">Filters</span>
        {filterCount > 0 ? (
          <span className="pill blue filter-toggle-badge" style={{ padding: "1px 6px", fontSize: 10 }}>
            <span>{filterCount}</span>
            <span className="filter-toggle-applied"> applied</span>
          </span>
        ) : null}
      </button>
    </div>
  );
}

function StepperRow({
  title,
  sub,
  value,
  min,
  onChange,
  last,
}: {
  title: string;
  sub: string;
  value: number;
  min: number;
  onChange: (v: number) => void;
  last?: boolean;
}) {
  return (
    <div className={`sb-who-row${last ? " last" : ""}`}>
      <div>
        <div className="sb-who-title">{title}</div>
        <div className="sb-who-sub">{sub}</div>
      </div>
      <div className="sb-who-stepper" onClick={(e) => e.stopPropagation()}>
        <button
          type="button"
          aria-label={`Decrease ${title}`}
          onClick={() => onChange(Math.max(min, value - 1))}
          disabled={value <= min}
        >
          −
        </button>
        <span aria-live="polite">{value}</span>
        <button
          type="button"
          aria-label={`Increase ${title}`}
          onClick={() => onChange(value + 1)}
        >
          +
        </button>
      </div>
    </div>
  );
}
